import React, { useEffect, useState } from "react";
import { Group, Modal, Select, Stack, Text, TextInput } from "@mantine/core";
import { Alert, Button } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import { errorMessage } from "@hooks/monitoring/useApiResource";

interface NameModalProps {
  opened: boolean;
  title: string;
  hint?: string;
  nameLabel: string;
  submitLabel: string;
  /** When given, a required select shown above the name (the licence of a new instance). */
  choice?: { label: string; options: { value: string; label: string }[] };
  onClose: () => void;
  onSubmit: (name: string, choice: string | null) => Promise<unknown>;
}

/** A small "name it" form: a new licence instance (with its licence) or a new API key. */
const NameModal: React.FC<NameModalProps> = ({
  opened, title, hint, nameLabel, submitLabel, choice, onClose, onSubmit,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setName("");
    setPicked(choice?.options.length === 1 ? choice.options[0].value : null);
    setError(null);
  }, [opened, choice]);

  const incomplete = !name.trim() || (choice !== undefined && !picked);
  const submit = async () => {
    if (incomplete) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(name.trim(), picked);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="md" portalProps={{ target: "#admin-portal-root" }}>
      <Stack gap="md">
        {hint && <Text size="sm" c="dimmed">{hint}</Text>}
        {choice && (
          <Select label={choice.label} data={choice.options} value={picked} onChange={setPicked} required
                  allowDeselect={false} comboboxProps={{ portalProps: { target: "#admin-portal-root" } }} />
        )}
        <TextInput label={nameLabel} value={name} required data-autofocus
                   onChange={(e) => setName(e.currentTarget.value)}
                   onKeyDown={(e) => e.key === "Enter" && void submit()} />
        {error && <Alert color="red">{error}</Alert>}
        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={onClose} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={submit} loading={saving} disabled={incomplete}>{submitLabel}</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default NameModal;
